import React from 'react';
import { Box, Chip, Typography, Stack } from '@curves/ui-base';
import { Grid } from '@mui/material';
import MeasurementBar from '../Common/MeasurementBar';


/**
 * MetricRangeCard - 測量指標範圍卡片組件
 * 
 * 顯示測量指標的目前數值、上次數值、標準範圍，並以視覺化長條圖顯示數值在標準範圍內的位置。
 * 會自動顯示數值變化趨勢（上升 ▲ 或下降 ▼）。
 * 
 * @component
 * @param {Object} props - 組件屬性
 * @param {string} props.title - 指標標題（例如：'骨骼肌質量 (SMM)'、'體脂肪率' 等）
 * @param {number} props.currentValue - 本次測量數值
 * @param {number} props.previousValue - 上次測量數值
 * @param {number} props.standardRangeMin - 標準範圍最小值
 * @param {number} props.standardRangeMax - 標準範圍最大值
 * @param {string} props.unitLabel - 單位標籤（例如：'kg'、'%'、'cm' 等）
 * @param {number} [props.minValue=0] - 長條圖顯示的最小值，預設為 0
 * @param {number} [props.maxValue=100] - 長條圖顯示的最大值，預設為 100
 * @returns {JSX.Element} MetricRangeCard 組件
 * 
 * @example
 * ```jsx
 * <MetricRangeCard
 *   title="骨骼肌質量 (SMM)"
 *   previousValue={24.3}
 *   currentValue={24.5}
 *   standardRangeMin={21.5}
 *   standardRangeMax={26.3}
 *   unitLabel="kg"
 *   minValue={0}
 *   maxValue={50}
 * />
 * ```
 */
const MetricRangeCard = ({ title, currentValue, previousValue, standardRangeMin, standardRangeMax, unitLabel, minValue, maxValue }) => {
    return (
        <Box className="metric-range-card">
            <Stack className="metric-range-header" justifyContent="space-between" alignItems="center" spacing={2}>
                <Typography className="title">{title}</Typography>
                <Chip className="chip" label="標準" color="success" />
            </Stack>
            <Grid className="stats" container spacing={2}>
                <Grid xs={3} item>
                    <Box className="col">
                        <Typography className="label">上次</Typography>
                        <Typography className="value-sm">
                            {previousValue} {unitLabel}
                        </Typography>
                    </Box>
                </Grid>
                <Grid xs={6} item>
                    <Box className="col">
                        <Stack className="trend" justifyContent="center" alignItems="center" direction="row">
                            本次數值
                            {currentValue > previousValue && <span className="ml-1">▲</span>}
                            {currentValue < previousValue && <span className="ml-1">▼</span>}
                        </Stack>
                        <Typography className="value-lg">
                            {currentValue}
                            <span className="unit-lg">{unitLabel}</span>
                        </Typography>
                    </Box>
                </Grid>
                <Grid xs={3} item>
                    <Box className="col">
                        <Typography className="label">單位</Typography>
                        <Typography className="value-sm">{unitLabel === '%' ? '%' : '公斤'}</Typography>
                    </Box>
                </Grid>
            </Grid>
            <MeasurementBar
                className="metric-range-bar"
                value={currentValue}
                normalRange={[standardRangeMin, standardRangeMax]}
                minValue={minValue || 0}
                maxValue={maxValue || 100}
                showLabels
            />
            <Typography className="footer-text">
                正常範圍: {standardRangeMin} ~ {standardRangeMax} {unitLabel}
            </Typography>
        </Box>
    );
};

export default MetricRangeCard;
